import React, { useCallback, useMemo, useState } from 'react';
import { Form, OverlayTrigger } from 'react-bootstrap';
import { useField, useFormikContext, FieldAttributes } from 'formik';

import { FieldLabel } from './../index';

import FieldTooltip from './field-tooltip';
import { FieldFeedback } from '.';

type Props = {
  as?: React.ElementType<any>;
  label: string;
  placeholder?: string;
  /** Γραμμές σε συρρικνωμένη κατάσταση */
  rows?: number;
  /** Γραμμές όταν είναι ανοιχτό το πεδίο */
  expandedRows?: number;
  /**
   * Αν true (default), εμφανίζει +/- δίπλα στο label όταν το κείμενο
   * ξεπερνά τις ορατές γραμμές — για άνετη ανάγνωση χωρίς στενό scroll.
   */
  expandable?: boolean;
  disabled?: boolean;
  required?: boolean;
  resize?: boolean;
} & FieldAttributes<any>;

/** Εκτίμηση αν το κείμενο ξεπερνά τις ορατές γραμμές του textarea */
function isTextLong(text: string, visibleRows: number): boolean {
  const trimmed = text.trim();
  if (!trimmed) return false;
  const lineCount = trimmed.split(/\r?\n/).length;
  // ~45 χαρακτήρες ανά γραμμή σε τυπικό form column
  const approxWrapped = Math.ceil(trimmed.length / 45);
  return lineCount > visibleRows || approxWrapped > visibleRows;
}

const FieldTextArea = ({
  as = 'div',
  name,
  label,
  placeholder = '',
  rows = 1,
  expandedRows = 8,
  expandable = true,
  resize = false,
  disabled = false,
  required = false,
}: Props) => {
  const [{ value }, { touched, error, initialValue }] = useField<string>(name);
  const { setFieldValue, setFieldTouched } = useFormikContext<any>();
  const [expanded, setExpanded] = useState(false);

  const text = String(value ?? '');
  const isLong = useMemo(() => isTextLong(text, rows), [text, rows]);
  const showExpand = expandable && isLong;
  const effectiveRows = showExpand && expanded ? expandedRows : rows;
  const canResize = resize || (showExpand && expanded);
  const changed = value !== initialValue;

  const setFieldValueAndTouched = useCallback(
    async (nextValue, nextTouched: boolean) => {
      await setFieldValue(name, nextValue, true);
      await setFieldTouched(name, nextTouched);
    },
    [name, setFieldTouched, setFieldValue]
  );

  const handleChange = useCallback(
    (event) => {
      setFieldValueAndTouched(event.target.value, true);
    },
    [setFieldValueAndTouched]
  );

  const handleBlur = useCallback(async () => {
    await setFieldTouched(name, touched);
  }, [setFieldTouched, name, touched]);

  const expandToggle = showExpand ? (
    <button
      type="button"
      className="btn btn-link p-0 border-0 bg-transparent text-decoration-none d-inline-flex align-items-center justify-content-center"
      style={{
        width: 16,
        height: 16,
        fontSize: '0.95rem',
        fontWeight: 600,
        lineHeight: 1,
        color: 'var(--bs-secondary)',
        flexShrink: 0,
      }}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setExpanded((v) => !v);
      }}
      aria-expanded={expanded}
      aria-label={expanded ? 'Σύμπτυξη κειμένου' : 'Επέκταση κειμένου'}
      title={expanded ? 'Λιγότερα' : 'Περισσότερα'}
    >
      {expanded ? '−' : '+'}
    </button>
  ) : null;

  return (
    <Form.Group as={as} controlId={name} className="mb-2">
      <FieldLabel text={label} changed={changed} required={required} endAdornment={expandToggle} />
      <OverlayTrigger
        delay={{ show: 1000, hide: 200 }}
        overlay={<FieldTooltip text={name} value={value || ''} />}
        placement="top"
      >
        <Form.Control
          className="mb-1"
          as="textarea"
          style={{ resize: !canResize ? 'none' : undefined }}
          rows={effectiveRows}
          name={name}
          value={value || ''}
          placeholder={placeholder}
          isInvalid={touched && !!error}
          disabled={disabled}
          onChange={handleChange}
          onBlur={handleBlur}
        />
      </OverlayTrigger>
      <FieldFeedback type="invalid" name={name} />
    </Form.Group>
  );
};

export default FieldTextArea;
